import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { CardT, GameLobbyPhaseT, GamePhaseT, GamePlayPhaseT, GameStateT } from '@/types/Game'
import { useAppStore } from './app'
import { useUserStore } from './user'
import type { PlayerT } from '@/types/User'

export const useGameStore = defineStore('game', () => {
  const appStore = useAppStore()
  const gameState = ref<GameStateT | null>(null)
  const gameUpdatedAt = ref<Date | null>(null)
  const deferredGameState = ref<GameStateT | null>(null)
  const allCards = ref<CardT[] | null>(null)
  const MAX_PLAYERS = ref(4)
  const MIN_PLAYERS = ref(2)
  const updatedPlayersHandArrangement = ref<CardT[] | null>(null)

  const mixArrangedCardsAndStateCards = (arrangedCards: CardT[] | null, stateCards: string[] | undefined) => {
    if (!arrangedCards || !stateCards) {
      return arrangedCards
    }

    const stateCardIds = new Set(stateCards)

    // Check if all arranged cards are still in state
    const validArrangedCards = arrangedCards.filter(card => stateCardIds.has(card.id))
    if (validArrangedCards.length === arrangedCards.length && validArrangedCards.length === stateCards.length) {
      return arrangedCards
    }

    // Get IDs from valid arranged cards
    const arrangedCardIds = new Set(validArrangedCards.map(card => card.id))
    const newCardIds = stateCards.filter(cardId => !arrangedCardIds.has(cardId))
    const newCards = newCardIds.map(cardId => getCardById(cardId)).filter(card => card !== null) as CardT[]

    return [...validArrangedCards, ...newCards]
  }

  const updateGameState = (newState: GameStateT | null) => {
    gameState.value = newState
    gameUpdatedAt.value = new Date()
  }

  const updateDeferredGameState = (newState: GameStateT | null) => {
    deferredGameState.value = newState
  }

  const getCardById = (cardId: string): CardT | null => {
    if (allCards.value) {
      return allCards.value.find(card => card.id === cardId) || null
    }
    return null
  }

  const arrangePlayerHand = (cards: CardT[]) => {
    updatedPlayersHandArrangement.value = cards
  }

  const allPlayers = computed<PlayerT[] | null>(() => {
    if (gameState.value) {
      return gameState.value.players.map(player => ({
        ...player,
        username: player.type === 'bot' ? player.username.charAt(0).toUpperCase() + player.username.slice(1) : player.username
      }))
    }
    return null
  })

  const getPlayerById = (playerId: string): PlayerT | null => {
    if (allPlayers.value) {
      return allPlayers.value.find(player => player.id === playerId) || null
    }
    return null
  }

  // Profile of the player whose turn it is
  const currentTurnPlayerProfile = computed(() => {
    if (gameState.value) {
      return allPlayers.value![gameState.value.currentTurn] || null
    }
    return null
  })

  // Profile of the user playing the game
  const userPlayerProfile = computed(() => {
    const userStore = useUserStore()
    if (gameState.value) {
      return gameState.value.players.find(p => p.id === userStore.user?.id) || null
    }
    return null
  })

  const currentUserHasPendingTradeOffer = computed(() => {
    const userStore = useUserStore()
    if (gameState.value && userStore.user) {
      const userId = userStore.user.id
      const activeTrade = gameState.value.activeTrade
      if (activeTrade) {
        const userOffer = activeTrade.offers.find(offer => offer.playerId === userId)
        return userOffer?.status === 'pending'
      }
    }
    return false
  })

  // Profiles of other players in the game
  const otherPlayersProfiles = computed(() => {
    const userStore = useUserStore()
    if (gameState.value) {
      return gameState.value.players.filter(p => p.id !== userStore.user?.id)
    }
    return null
  })

  // If it's the user's turn
  const isTurn = computed(() => {
    const userStore = useUserStore()
    if (gameState.value) {
      const playerIndex = gameState.value.players.findIndex(p => p.id === userStore.user?.id)

      return gameState.value.currentTurn == playerIndex
    }
    return false
  })

  // If the user is waiting for their turn
  const isWaitingTurn = computed(() => !isTurn.value)

  const playStatus = computed(() => gameState.value?.playStatus)

  const lastGamePlayerID = computed(() => gameState.value?.prev_player_id || '')

  const lastGameAction = computed(() => {
    if (gameState.value) {
      if (lastGamePlayerID.value && lastGamePlayerID.value !== userPlayerProfile.value?.id) {
        return gameState.value.prev_player_action
      }
    }

    return ''
  })

  // Computed properties for game phases
  const gamePhase = computed<GamePhaseT>(() => {
    if (appStore.showLearning) return 'GAME_LEARNING_PHASE'
    if (appStore.showSettings) return 'GAME_SETTINGS_PHASE'
    if (appStore.showLeaderboard) return 'GAME_LEADERBOARD_PHASE'

    if (!gameState.value) return 'GAME_LOBBY_PHASE'
    if (gameState.value.status == 'pending') return 'GAME_LOBBY_PHASE'
    if (gameState.value.status == 'active') return 'GAME_PLAY_PHASE'
    if (gameState.value.status == 'ended') return 'GAME_ENDED_PHASE'

    return 'GAME_LOBBY_PHASE'
  })

  // Computed properties for lobby and gameplay screens
  const activeLobbyPhaseScreen = computed<GameLobbyPhaseT>(() => {
    if (gameState.value) return 'LOBBY_SCREEN'
    return 'GAME_INTRO_SCREEN'
  })

  // Computed properties for active gameplay screen
  const activeGamePlayScreen = computed<GamePlayPhaseT>(() => {
    if (!gameState.value || playStatus.value == 'waiting') return 'GAME_AREA_SCREEN'

    if (isTurn.value) {
      if (playStatus.value == 'finalizing_trade') {
        return 'FINALISING_TRADE_CARD_SCREEN'
      }
      return 'GAME_AREA_SCREEN'
    } else {
      if (playStatus.value == 'offering_trade' && currentUserHasPendingTradeOffer.value) {
        return 'CARD_REQUESTED_SCREEN'
      }
      return 'GAME_AREA_SCREEN'
    }
  })

  // Cards in the user's hand
  const userPlayersHand = computed<CardT[] | null>(() => {
    // Mapping players card to CardT[]
    if (gameState.value) {
      const userHand = userPlayerProfile.value?.hand
      if (userHand && allCards.value) {
        return userHand.map((id: string) => allCards.value!.find((card: CardT) => card.id === id)) as CardT[]
      }
    }
    return null
  })

  const userPlayersHandArranged = computed<CardT[] | null>(() => {
    if (updatedPlayersHandArrangement.value) {
      const tempUpdated = mixArrangedCardsAndStateCards(updatedPlayersHandArrangement.value, userPlayerProfile.value?.hand)
      return tempUpdated
    }
    return userPlayersHand.value
  })

  // Active deck
  const activeDeck = computed(() => {
    // Mapping activeDeck to CardT[]
    if (gameState.value) {
      const deck = gameState.value.activeDeck
      if (deck && allCards.value) {
        return deck.map((id: string) => allCards.value!.find((card: CardT) => card.id === id)) as CardT[]
      }
    }
    return null
  })

  // Spent deck
  const spentDeck = computed(() => {
    // Mapping spentDeck to CardT[]
    if (gameState.value) {
      const deck = gameState.value.spentDeck
      if (deck && allCards.value) {
        return deck.map((id: string) => allCards.value!.find((card: CardT) => card.id === id)) as CardT[]
      }
    }
    return null
  })

  const getActionTimeout = () => {
    if (userPlayerProfile.value) {
      const timeout = userPlayerProfile.value.actionTimeout

      // Get current time and the time when the game was last updated
      const currentTime = new Date().getTime()
      const lastUpdatedTime = gameUpdatedAt.value ? gameUpdatedAt.value.getTime() : currentTime

      // Calculate elapsed time in seconds
      const elapsedSeconds = (currentTime - lastUpdatedTime) / 1000

      // Calculate remaining timeout
      const remainingTimeout = Math.max(0, timeout - elapsedSeconds)

      return remainingTimeout
    }
    return 0
  }

  const resumeGameState = () => {
    if (deferredGameState.value) {
      gameState.value = deferredGameState.value
      deferredGameState.value = null
      appStore.isDeferringState = false
    }
  }

  const resetGame = () => {
    gameState.value = null
    deferredGameState.value = null
    appStore.isDeferringState = false
  }

  return {
    gameState,
    deferredGameState,
    gamePhase,
    isTurn,
    isWaitingTurn,
    updateGameState,
    updateDeferredGameState,
    resumeGameState,
    arrangePlayerHand,
    activeLobbyPhaseScreen,
    allCards,
    activeGamePlayScreen,
    allPlayers,
    MAX_PLAYERS,
    MIN_PLAYERS,
    currentTurnPlayerProfile,
    userPlayerProfile,
    spentDeck,
    activeDeck,
    userPlayersHand,
    userPlayersHandArranged,
    otherPlayersProfiles,
    resetGame,
    playStatus,
    getCardById,
    getPlayerById,
    currentUserHasPendingTradeOffer,
    getActionTimeout,
    lastGameAction,
    lastGamePlayerID
  }
})
